import React, { useRef, useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { useReactToPrint } from "react-to-print"
import * as XLSX from "xlsx"
import CommonSelect from "../../../core/common/commonSelect"
import Table from "../../../core/common/dataTable/index"
import TooltipOption from "../../../core/common/tooltipOption"
import { useSelector } from "react-redux"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos"
import {
  getAllCourses,
  createCourse,
  updateCourse,
  deleteCourse,
  Course,
  CourseCreateData,
  CourseUpdateData,
} from "../../../service/courses"
import { all_routes } from "../../router/all_routes"
import { toast } from "react-toastify"

const Courses = () => {
  const componentRef = useRef<HTMLDivElement>(null)
  const currentBranch = useSelector(selectCurrentBranch)
  const [loading, setLoading] = useState(false)
  const [courses, setCourses] = useState<Course[]>([])
  const [searchText, setSearchText] = useState("")
  const [formData, setFormData] = useState<CourseCreateData>({
    title: "",
    courseIdentifier: "",
  })
  const [editFormData, setEditFormData] = useState<CourseUpdateData>({})
  const [selectedCourseId, setSelectedCourseId] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handlePrint = useReactToPrint({ contentRef: componentRef })

  const fetchCourses = async () => {
    setLoading(true)
    try {
      const response = await getAllCourses({
        searchText,
      })
      setCourses(response.data || [])
    } catch (error) {
      console.error("Error fetching courses:", error)
      toast.error("Failed to load courses")
      setCourses([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCourses()
  }, [searchText])

  const handleCreateCourse = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title || !formData.courseIdentifier) {
      toast.error("Please fill all required fields")
      return
    }

    setIsSubmitting(true)
    try {
      await createCourse(formData)
      toast.success("Course created successfully!")
      setFormData({
        title: "",
        courseIdentifier: "",
      })
      const closeButton = document.querySelector(
        '#add_course [data-bs-dismiss="modal"]'
      ) as HTMLElement
      if (closeButton) closeButton.click()
      fetchCourses()
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || error.response?.data?.message || "Failed to create course"
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateCourse = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editFormData.title || !editFormData.courseIdentifier) {
      toast.error("Please fill all required fields")
      return
    }

    setIsSubmitting(true)
    try {
      await updateCourse(selectedCourseId, editFormData)
      toast.success("Course updated successfully!")
      setEditFormData({})
      setSelectedCourseId("")
      const closeButton = document.querySelector(
        '#edit_course [data-bs-dismiss="modal"]'
      ) as HTMLElement
      if (closeButton) closeButton.click()
      fetchCourses()
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || error.response?.data?.message || "Failed to update course"
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteCourse = async () => {
    if (!selectedCourseId) return

    setIsSubmitting(true)
    try {
      await deleteCourse(selectedCourseId)
      toast.success("Course deleted successfully!")
      setSelectedCourseId("")
      const closeButton = document.querySelector(
        '#delete_course [data-bs-dismiss="modal"]'
      ) as HTMLElement
      if (closeButton) closeButton.click()
      fetchCourses()
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || error.response?.data?.message || "Failed to delete course"
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const prepareEditCourse = (course: Course) => {
    setSelectedCourseId(course._id)
    setEditFormData({
      title: course.title,
      courseIdentifier: course.courseIdentifier,
    })
  }

  const prepareDeleteCourse = (courseId: string) => {
    setSelectedCourseId(courseId)
  }

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      courses.map(course => ({
        "Course ID": course.courseIdentifier || course._id,
        "Course Title": course.title || "N/A",
        "Created Date": course.createdAt ? new Date(course.createdAt).toLocaleDateString() : "N/A",
      }))
    )
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Courses")
    XLSX.writeFile(wb, "courses.xlsx")
  }

  const route = all_routes

  const columns = [
    {
      title: "Course Identifier",
      dataIndex: "courseIdentifier",
      sorter: true,
      render: (identifier: string) => identifier || "N/A",
    },
    {
      title: "Title",
      dataIndex: "title",
      sorter: true,
      render: (title: string) => title || "N/A",
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: Course) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#edit_course"
                  onClick={() => prepareEditCourse(record)}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1 text-danger"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#delete_course"
                  onClick={() => prepareDeleteCourse(record._id)}
                >
                  <i className="ti ti-trash me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Courses</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={route.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Academic</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    All Courses
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <TooltipOption
                  onRefresh={fetchCourses}
                  onExportPdf={handlePrint}
                  onExportExcel={handleExportExcel}
                />
                <button
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_course"
                >
                  <i className="ti ti-plus me-2" />
                  Add Course
                </button>
              </div>
            </div>
          </div>

          {/* Courses List */}
          <div className="card">
            <div className="card-header">
              <h4 className="mb-0">Courses List</h4>
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Class Courses</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              <Table
                columns={columns}
                dataSource={courses}
                // pagination={false}
                loading={loading}
                setSearchText={setSearchText}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Course Modal */}
      <div className="modal fade" id="add_course">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Course</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleCreateCourse}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Course Identifier <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.courseIdentifier}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            courseIdentifier: e.target.value.toUpperCase(),
                          }))
                        }
                        placeholder="e.g., MATH-101, PHY-09"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Course"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Course Modal */}
      <div className="modal fade" id="edit_course">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Course</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdateCourse}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editFormData.title || ""}
                        onChange={e =>
                          setEditFormData(prev => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Course Identifier <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={editFormData.courseIdentifier || ""}
                        onChange={e =>
                          setEditFormData(prev => ({
                            ...prev,
                            courseIdentifier: e.target.value.toUpperCase(),
                          }))
                        }
                        placeholder="e.g., MATH-101, PHY-09"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" id="delete_course">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form>
              <div className="modal-body text-center">
                <span className="delete-icon">
                  <i className="ti ti-trash-x" />
                </span>
                <h4>Confirm Deletion</h4>
                <p>
                  You want to delete this course? This action cannot be undone.
                </p>
                <div className="d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteCourse}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Courses
